import { z } from 'zod';
import { convertStoredJsonNumberToBoundedNumber } from './stored-json-number';

/**
 * One exact condition used to identify a physical stored JSON variant.
 * Multiple guards may be combined by a stored contract as a conjunction.
 */
export type StoredJsonVariantGuard =
    | {
          readonly kind: 'json_marker_absent';
          readonly markerPath: readonly [string, ...string[]];
      }
    | {
          readonly kind: 'json_marker_equals';
          readonly markerPath: readonly [string, ...string[]];
          readonly markerValue: string | number | boolean;
      }
    | {
          readonly kind: 'json_marker_absent_or_equals';
          readonly markerPath: readonly [string, ...string[]];
          readonly markerValue: string | number | boolean;
      }
    | {
          readonly kind: 'sibling_field_equals';
          readonly siblingField: string;
          readonly siblingValue: string | number | boolean;
      }
    | {
          readonly kind: 'sibling_field_present';
          readonly siblingField: string;
      };

/**
 * Physical JSON object-key paths for one variant's queryable semantic fields.
 */
export type StoredJsonSemanticPathMap<SemanticField extends string> = {
    readonly [Field in SemanticField]: readonly [string, ...string[]];
};

export declare const StoredJsonSemanticFieldValueTypeSymbol: unique symbol;

export type StoredJsonSemanticFieldValueKind<Value> = undefined extends Value
    ? never
    : [Exclude<Value, null>] extends [string | number | boolean]
      ? 'scalar'
      : Value extends readonly unknown[]
        ? 'array'
        : Value extends object
          ? 'object'
          : never;

export type StoredJsonSemanticFieldValue<Value> = {
    readonly kind: StoredJsonSemanticFieldValueKind<Value>;
    readonly [StoredJsonSemanticFieldValueTypeSymbol]?: Value;
};

export type StoredJsonSemanticFieldValueType<
    Definition extends StoredJsonSemanticFieldValue<unknown>,
> = Definition[typeof StoredJsonSemanticFieldValueTypeSymbol];

export type StoredJsonRowBoundary = {
    readonly value: unknown;
    readonly siblingValues: Readonly<Record<string, unknown>>;
};

export const defineStoredJsonSemanticFieldValue = <Value>(
    kind: StoredJsonSemanticFieldValueKind<Value>
): StoredJsonSemanticFieldValue<Value> => ({ kind });

type InferredStoredJsonSemanticFieldValue = {
    readonly kind: 'scalar' | 'array' | 'object';
    readonly [StoredJsonSemanticFieldValueTypeSymbol]?: unknown;
};

type InferredStoredJsonSemanticFieldValueMap = Readonly<
    Record<string, InferredStoredJsonSemanticFieldValue>
>;

/**
 * The complete physical-variant contract for one persisted JSON value.
 */
export type StoredJsonContractDefinition<
    CurrentType,
    VariantInputByName extends object,
    SemanticFieldValues extends InferredStoredJsonSemanticFieldValueMap = Record<never, never>,
> = {
    readonly contractName: string;
    readonly semanticFieldValues: SemanticFieldValues;
    readonly variants: {
        readonly [VariantName in keyof VariantInputByName]: {
            readonly schemaName: string;
            readonly schema: z.ZodType<VariantInputByName[VariantName]>;
            readonly schemaInput?: (
                boundary: StoredJsonRowBoundary
            ) => unknown;
            readonly guards: readonly StoredJsonVariantGuard[];
            readonly normalize: (input: VariantInputByName[VariantName]) => CurrentType;
            readonly semanticPaths: Partial<
                StoredJsonSemanticPathMap<Extract<keyof SemanticFieldValues, string>>
            >;
            readonly unrepresentedSemanticFields: readonly Extract<
                keyof SemanticFieldValues,
                string
            >[];
        };
    };
    readonly writerVariant: Extract<keyof VariantInputByName, string>;
};

type InferredStoredJsonVariant = {
    readonly schemaName: string;
    readonly schema: z.ZodType<unknown>;
    readonly schemaInput?: (
        boundary: StoredJsonRowBoundary
    ) => unknown;
    readonly guards: readonly StoredJsonVariantGuard[];
    readonly normalize: (input: never) => unknown;
    readonly semanticPaths: Readonly<Record<string, readonly [string, ...string[]]>>;
    readonly unrepresentedSemanticFields: readonly string[];
};

type InferredStoredJsonContract = {
    readonly contractName: string;
    readonly semanticFieldValues: InferredStoredJsonSemanticFieldValueMap;
    readonly variants: Readonly<Record<string, InferredStoredJsonVariant>>;
    readonly writerVariant: string;
};

type VariantSemanticFields<Variant extends InferredStoredJsonVariant> =
    | Extract<keyof Variant['semanticPaths'], string>
    | Variant['unrepresentedSemanticFields'][number];

type InvalidSemanticVariantNames<
    Variants extends InferredStoredJsonContract['variants'],
    SemanticField extends string,
> = {
    [VariantName in keyof Variants]:
        | Exclude<SemanticField, VariantSemanticFields<Variants[VariantName]>>
        | Exclude<VariantSemanticFields<Variants[VariantName]>, SemanticField>
        | Extract<
              Extract<keyof Variants[VariantName]['semanticPaths'], string>,
              Variants[VariantName]['unrepresentedSemanticFields'][number]
          > extends never
        ? never
        : VariantName;
}[keyof Variants];

type CompleteStoredJsonContract<Definition extends InferredStoredJsonContract> =
    Definition['writerVariant'] extends Extract<keyof Definition['variants'], string>
        ? InvalidSemanticVariantNames<
              Definition['variants'],
              Extract<keyof Definition['semanticFieldValues'], string>
          > extends never
            ? unknown
            : never
        : never;

export const defineStoredJsonContract = <const Definition extends InferredStoredJsonContract>(
    definition: Definition & CompleteStoredJsonContract<Definition>
): Definition => definition;

export const StoredJsonUnknownRecordSchema: z.ZodType<Record<string, unknown>> = z.record(
    z.string(),
    z.unknown()
);

export const StoredJsonSafeIntegerMarkerSchema: z.ZodType<number> = z
    .number()
    .int()
    .safe();

function storedJsonMarkerEquals(
    markerValue: unknown,
    expectedValue: string | number | boolean,
    contractName: string,
    markerPath: readonly string[]
): boolean {
    const expectedNumberResult = StoredJsonSafeIntegerMarkerSchema.safeParse(
        expectedValue
    );
    if (!expectedNumberResult.success) {
        return markerValue === expectedValue;
    }

    return convertStoredJsonNumberToBoundedNumber(markerValue, {
        domain: `${contractName}.${markerPath.join('.')}`,
        numericKind: 'safe-integer',
        minimum: expectedNumberResult.data,
        maximum: expectedNumberResult.data,
    }).success;
}

/**
 * Builds the stored-data interpreter for one versioned JSON contract.
 *
 * The parser consumes a row-boundary object with a `value` property and an
 * optional `siblingValues` record. It returns only the current semantic type.
 */
export function buildStoredJsonParser<const Definition extends InferredStoredJsonContract>(
    definition: Definition
): z.ZodType<ReturnType<Definition['variants'][keyof Definition['variants']]['normalize']>>;
export function buildStoredJsonParser<
    CurrentType,
    VariantInputByName extends object,
    SemanticFieldValues extends InferredStoredJsonSemanticFieldValueMap,
>(
    definition: StoredJsonContractDefinition<
        CurrentType,
        VariantInputByName,
        SemanticFieldValues
    >
): z.ZodType<CurrentType> {
    const StoredJsonParser: z.ZodType<CurrentType> = z
        .unknown()
        .transform((rowBoundary, ctx): CurrentType => {
            const boundaryParseResult = StoredJsonUnknownRecordSchema.safeParse(rowBoundary);
            if (
                !boundaryParseResult.success ||
                !Object.prototype.hasOwnProperty.call(boundaryParseResult.data, 'value')
            ) {
                ctx.addIssue({
                    code: 'custom',
                    message: `${definition.contractName} requires a stored row boundary`,
                });
                return z.NEVER;
            }

            const boundary: Record<string, unknown> = boundaryParseResult.data;
            const siblingValues: unknown = boundary.siblingValues;
            const siblingParseResult =
                siblingValues === undefined
                    ? StoredJsonUnknownRecordSchema.safeParse({})
                    : StoredJsonUnknownRecordSchema.safeParse(siblingValues);
            if (!siblingParseResult.success) {
                ctx.addIssue({
                    code: 'custom',
                    message: `${definition.contractName} sibling values must be an object`,
                    path: ['siblingValues'],
                });
                return z.NEVER;
            }

            const storedValue: unknown = boundary.value;
            const siblingRecord: Record<string, unknown> = siblingParseResult.data;
            const matchingVariantNames: Array<Extract<keyof VariantInputByName, string>> = [];

            for (const variantName in definition.variants) {
                const variant: StoredJsonContractDefinition<
                    CurrentType,
                    VariantInputByName,
                    SemanticFieldValues
                >['variants'][Extract<keyof VariantInputByName, string>] =
                    definition.variants[variantName];
                const guardsMatch: boolean = variant.guards.every((guard) => {
                    if (guard.kind === 'sibling_field_equals') {
                        return (
                            Object.prototype.hasOwnProperty.call(
                                siblingRecord,
                                guard.siblingField
                            ) && siblingRecord[guard.siblingField] === guard.siblingValue
                        );
                    }
                    if (guard.kind === 'sibling_field_present') {
                        return Object.prototype.hasOwnProperty.call(
                            siblingRecord,
                            guard.siblingField
                        );
                    }

                    let markerValue: unknown = storedValue;
                    let markerPresent = true;
                    for (const pathSegment of guard.markerPath) {
                        const markerRecordParseResult =
                            StoredJsonUnknownRecordSchema.safeParse(markerValue);
                        if (!markerRecordParseResult.success) {
                            markerPresent = false;
                            break;
                        }
                        const markerRecord: Record<string, unknown> = markerRecordParseResult.data;
                        if (!Object.prototype.hasOwnProperty.call(markerRecord, pathSegment)) {
                            markerPresent = false;
                            break;
                        }
                        markerValue = markerRecord[pathSegment];
                    }

                    if (guard.kind === 'json_marker_absent') {
                        return !markerPresent;
                    }
                    if (
                        guard.kind === 'json_marker_absent_or_equals' &&
                        !markerPresent
                    ) {
                        return true;
                    }
                    return (
                        markerPresent &&
                        storedJsonMarkerEquals(
                            markerValue,
                            guard.markerValue,
                            definition.contractName,
                            guard.markerPath
                        )
                    );
                });
                if (guardsMatch) {
                    matchingVariantNames.push(variantName);
                }
            }

            if (matchingVariantNames.length === 0) {
                ctx.addIssue({
                    code: 'custom',
                    message: `No supported ${definition.contractName} stored JSON variant matched`,
                    path: ['value'],
                });
                return z.NEVER;
            }

            if (matchingVariantNames.length > 1) {
                ctx.addIssue({
                    code: 'custom',
                    message: `Multiple ${definition.contractName} stored JSON variants matched`,
                    path: ['value'],
                });
                return z.NEVER;
            }

            function parseSelectedVariant<
                VariantName extends Extract<keyof VariantInputByName, string>,
            >(variantName: VariantName): CurrentType {
                const selectedVariant: StoredJsonContractDefinition<
                    CurrentType,
                    VariantInputByName,
                    SemanticFieldValues
                >['variants'][VariantName] = definition.variants[variantName];
                const schemaInput =
                    selectedVariant.schemaInput?.({
                        value: storedValue,
                        siblingValues: siblingRecord,
                    }) ?? storedValue;
                const parseResult = selectedVariant.schema.safeParse(schemaInput);
                if (!parseResult.success) {
                    for (const issue of parseResult.error.issues) {
                        ctx.addIssue({
                            code: 'custom',
                            message: `${selectedVariant.schemaName}: ${issue.message}`,
                            path: ['value', ...issue.path],
                        });
                    }
                    return z.NEVER;
                }

                return selectedVariant.normalize(parseResult.data);
            }

            const selectedVariantName = matchingVariantNames[0];
            if (selectedVariantName === undefined) {
                ctx.addIssue({
                    code: 'custom',
                    message: `No supported ${definition.contractName} stored JSON variant matched`,
                    path: ['value'],
                });
                return z.NEVER;
            }
            return parseSelectedVariant(selectedVariantName);
        });

    return StoredJsonParser;
}

/**
 * Builds a stored-data interpreter for a JSON value whose variant guards do
 * not depend on sibling relational fields. Error paths remain relative to the
 * JSON value instead of exposing the internal row-boundary wrapper.
 */
export function buildStoredJsonValueParser<
    const Definition extends InferredStoredJsonContract,
>(
    definition: Definition
): z.ZodType<
    ReturnType<
        Definition['variants'][keyof Definition['variants']]['normalize']
    >
> {
    type CurrentType = ReturnType<
        Definition['variants'][keyof Definition['variants']]['normalize']
    >;
    const rowParser: z.ZodType<CurrentType> =
        buildStoredJsonParser(definition);

    return z.unknown().transform((value, ctx): CurrentType => {
        const parseResult = rowParser.safeParse({ value });
        if (parseResult.success) {
            return parseResult.data;
        }

        for (const issue of parseResult.error.issues) {
            ctx.addIssue({
                code: 'custom',
                message: issue.message,
                path:
                    issue.path[0] === 'value'
                        ? issue.path.slice(1)
                        : [...issue.path],
            });
        }
        return z.NEVER;
    });
}
